// Sticky glass navbar. Minimal: logo + (Go Live for publishers) + account menu
// (login when anonymous). Creator Studio lives in the account menu, publisher-only.
import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LayoutDashboard, LogOut, Radio, User } from "lucide-react";
import { useLogout, useMe } from "../api/hooks.js";
import { hasPublish } from "../api/types.js";
import { env } from "../env.js";
import { useLoginModal } from "../ui/loginModal.js";
import { Button } from "./ui/button.js";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu.js";
import { CreateStreamModal } from "./CreateStreamModal.js";
import { LanguageToggle } from "./LanguageToggle.js";
import { Avatar } from "./ui/Avatar.js";

export function Navbar() {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const { openLogin } = useLoginModal();
  const logout = useLogout();
  const [createOpen, setCreateOpen] = useState(false);

  const canGoLive = me ? hasPublish(me.capabilities) : false;

  return (
    <header className="sticky top-0 z-40 border-b border-white/[0.06] bg-background/70 backdrop-blur-xl">
      <div className="mx-auto flex h-14 max-w-7xl items-center gap-3 px-4">
        <Link
          to="/"
          className="flex items-center gap-2 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Radio className="h-4 w-4" />
          </span>
          <span className="font-display text-lg font-bold tracking-tight text-foreground">
            {env.siteName}
          </span>
        </Link>

        <div className="ml-auto flex items-center gap-2.5">
          {canGoLive && (
            <Button variant="primary" size="sm" onClick={() => setCreateOpen(true)}>
              <Radio className="h-4 w-4" />
              {t("nav.goLive")}
            </Button>
          )}

          {me ? (
            <DropdownMenu>
              <DropdownMenuTrigger className="rounded-full outline-none ring-offset-background transition focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 data-[state=open]:ring-2 data-[state=open]:ring-primary/50">
                <Avatar src={me.avatarUrl} name={me.displayName} size={32} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel>{me.displayName}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/me">
                    <User className="h-4 w-4" />
                    {t("nav.profile")}
                  </Link>
                </DropdownMenuItem>
                {/* Creator Studio: publishers only. */}
                {canGoLive && (
                  <DropdownMenuItem asChild>
                    <Link to="/studio">
                      <LayoutDashboard className="h-4 w-4" />
                      {t("nav.creatorStudio")}
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <div className="flex items-center justify-between px-2.5 py-1.5">
                  <span className="text-xs text-muted-foreground">{t("lang.label")}</span>
                  <LanguageToggle />
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem destructive onSelect={() => logout.mutate()}>
                  <LogOut className="h-4 w-4" />
                  {t("nav.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="flex items-center gap-2.5">
              <LanguageToggle className="hidden sm:inline-flex" />
              <Button variant="outline" size="sm" onClick={openLogin}>
                {t("nav.login")}
              </Button>
            </div>
          )}
        </div>
      </div>

      <CreateStreamModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </header>
  );
}
